import { useUser } from "@features/user"
import Content from "components/Content"
import OffersActionButtons from "components/OffersActionButtons"
import TwoColumnLayout from "components/TwoColumnLayout"
import type { FC } from "react"
import { useState } from "react"
import { Trans } from "react-i18next"
import OffersSidebar from "../../components/OffersSidebar"
import useOffersFilter from "../../hooks/useOffersFilter"
import { useOffers } from "../offers"
import MenuOfferItem from "../offers/components/MenuOfferItem"
import OfferSkeleton from "../offersPdf/components/OfferSkeleton"
import EditOfferDialog from "./components/EditOfferDialog"
import HeaderPickerDialog from "./components/HeaderPickerDialog"
import NewsletterPreviewSection from "./components/NewsletterPreviewSection"
import { useNewsletterStore } from "./store/newsletterStore"

const Newsletter: FC = () => {
  const { data: offers = [], isLoading } = useOffers()
  const { data } = useUser()
  const {
    offers: filteredOffers,
    categories,
    programs,
    tags,
    updateFilter,
  } = useOffersFilter(offers || [])

  const header = useNewsletterStore((s) => s.header)
  const selected = useNewsletterStore((s) => s.selected)
  const headerUrl = useNewsletterStore((s) => s.headerUrl)
  const clear = useNewsletterStore((s) => s.clear)
  const toggle = useNewsletterStore((s) => s.toggle)
  const setHeader = useNewsletterStore((s) => s.setHeader)
  const isSelected = useNewsletterStore((s) => s.isSelected)
  const setHeaderUrl = useNewsletterStore((s) => s.setHeaderUrl)
  const reorderSelected = useNewsletterStore((s) => s.reorderSelected)

  const [pickerOpen, setPickerOpen] = useState(false)
  const [editOfferId, setEditOfferId] = useState<string | null>(null)
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null)

  const handleDragStart =
    (index: number) => (e: React.DragEvent<HTMLLIElement>) => {
      e.dataTransfer.effectAllowed = "move"
      e.dataTransfer.setData("text/plain", String(index))
      setDraggingIndex(index)
    }

  const handleDragOver = (e: React.DragEvent<HTMLLIElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }

  const handleDrop =
    (toIndex: number) => (e: React.DragEvent<HTMLLIElement>) => {
      e.preventDefault()
      const fromIndexString = e.dataTransfer.getData("text/plain")
      const fromIndex = Number(fromIndexString)
      if (!Number.isNaN(fromIndex)) {
        reorderSelected(fromIndex, toIndex)
      }
      setDraggingIndex(null)
    }

  const handleDragEnd = () => {
    setDraggingIndex(null)
  }

  const handleRemoveHeader = () => {
    setHeader(null)
    setHeaderUrl(null)
  }

  const handleMoveUp = (index: number) => {
    reorderSelected(index, index - 1)
  }

  const handleMoveDown = (index: number) => {
    reorderSelected(index, index + 1)
  }

  if (isLoading) {
    return (
      <div className="w-full h-full grid place-items-center p-2">
        <OfferSkeleton />
      </div>
    )
  }

  return (
    <Content title={<Trans i18nKey="newsletter.title" />}>
      <OffersActionButtons />

      <HeaderPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
      />

      <TwoColumnLayout
        sidebar={
          <OffersSidebar
            offers={filteredOffers}
            categories={categories}
            programs={programs}
            tags={tags}
            onFilterChange={updateFilter}
            renderOfferItem={(offer) => (
              <MenuOfferItem
                key={`${offer.Type}-${offer.CustomOfferID}`}
                offer={offer}
                checked={isSelected(offer)}
                onClick={() => toggle(offer)}
              />
            )}
          />
        }
        content={
          <NewsletterPreviewSection
            selected={selected}
            header={header}
            headerUrl={headerUrl}
            draggingIndex={draggingIndex}
            agency={data?.agency}
            onOpenPicker={() => setPickerOpen(true)}
            onRemoveHeader={handleRemoveHeader}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
            onEdit={setEditOfferId}
            onRemove={toggle}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
            onClear={clear}
          />
        }
      />
      <EditOfferDialog
        open={!!editOfferId}
        offerId={editOfferId ?? ""}
        onClose={() => setEditOfferId(null)}
      />
    </Content>
  )
}

export default Newsletter
